import { Node, mergeAttributes } from '@tiptap/core';
import { snakeCaseToCamelCase } from '../string/index.js';
const BUILTIN_SERVICES = {
    figma: {
        find: /^https:\/\/([\w\.-]+\.)?figma\.com\/(file|proto)\/([0-9a-zA-Z]{22,128})(?:\/.*)?$/g,
        urlToIFrameSrc(url) {
            return `https://www.figma.com/embed?embed_host=magicbrief&url=${url}`;
        },
        attributes(url) {
            return {
                url,
                src: this.urlToIFrameSrc(url),
                allow: 'fullscreen',
                allowFullscreen: true,
                style: { width: '100%', aspectRatio: '16 / 9' },
            };
        },
    },
    loom: {
        find: /^https:\/\/(www.)?loom\.com\/(share|embed)\/.+$/g,
        urlToIFrameSrc(url) {
            if (url.includes('/embed/')) {
                return url;
            }
            return url.replace('/share/', '/embed/');
        },
        attributes(url) {
            return {
                url,
                src: this.urlToIFrameSrc(url),
                allow: 'fullscreen',
                allowFullscreen: true,
                style: { maxWidth: '720px' },
            };
        },
    },
    spotify: {
        find: /^https:\/\/open\.spotify\.com\/(embed\/)?[a-zA-Z0-9\-\_^\/]+\/[^\/]+$/g,
        urlToIFrameSrc(url) {
            if (url.includes('/embed/')) {
                return url;
            }
            return url.replace('open.spotify.com', 'open.spotify.com/embed');
        },
        attributes(url) {
            return {
                url,
                src: this.urlToIFrameSrc(url),
                allow: 'autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture',
                allowFullscreen: true,
                height: /\/(track|episode)\//.test(url) ? '152' : '352', // If individual track/song/etc use compact view
                loading: 'lazy',
            };
        },
    },
    applemusic: {
        find: /^https:\/\/(embed\.)?music\.apple\.com\/[a-zA-Z]+\/.+\/.+$/g,
        urlToIFrameSrc(url) {
            if (url.includes('embed.music.apple.com')) {
                return url;
            }
            return url.replace('music.apple.com', 'embed.music.apple.com');
        },
        attributes(url) {
            return {
                url,
                src: this.urlToIFrameSrc(url),
                allow: 'autoplay *; encrypted-media *;',
                style: {
                    width: '100%',
                    maxWidth: '660px',
                    overflow: 'hidden',
                    background: 'transparent',
                },
                loading: 'lazy',
                height: /\?i=.+/.test(url) ? '150' : '450', // If individual track/song/etc use compact view
                sandbox: 'allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation',
            };
        },
    },
};
export const styleStringToCSSPropertiesObject = (str) => {
    const style = {};
    str.split(';').forEach((el) => {
        const [property, value] = el.split(':');
        if (!property)
            return;
        const formattedProperty = snakeCaseToCamelCase(property.trim());
        style[formattedProperty] = value.trim();
    });
    return style;
};
export const Embed = Node.create({
    name: 'embed',
    group: 'block',
    draggable: true,
    atom: true,
    addOptions() {
        return {
            mode: 'iframely',
            showEmbedInput: () => console.warn('showEmbedInput not implemented, must be provided by extension caller.'),
        };
    },
    // Uncomment this method if you want to enable auto-embedding of URLs with matchers
    // provided in the BUILTIN_SERVICES object defined above.
    //
    // addPasteRules() {
    //   const rules = Object.entries(BUILTIN_SERVICES).map(([_, value]) =>
    //     nodePasteRule({
    //       find: value.find,
    //       type: this.type,
    //       getAttributes: (match) => {
    //         if (!match.input) {
    //           return;
    //         }
    //         return value.attributes(match.input);
    //       },
    //     })
    //   );
    //   return rules;
    // },
    addAttributes() {
        return {
            url: {
                isRequired: true,
                renderHTML(attrs) {
                    return { 'data-url': attrs.url };
                },
                parseHTML(element) {
                    return element.getAttribute('data-url');
                },
            },
            src: {
                isRequired: true,
                renderHTML(attrs) {
                    return { src: attrs.src };
                },
                parseHTML(element) {
                    return element.getAttribute('src');
                },
            },
            allow: {
                renderHTML(attrs) {
                    return { allow: attrs.allow };
                },
                parseHTML(element) {
                    return element.getAttribute('allow');
                },
            },
            allowFullscreen: {
                renderHTML(attrs) {
                    return {
                        allowfullscreen: attrs.allowFullscreen ? 'true' : undefined,
                    };
                },
                parseHTML(element) {
                    return element.getAttribute('allowfullscreen') === 'true';
                },
            },
            className: {
                renderHTML(attrs) {
                    return { class: attrs.className };
                },
                parseHTML(element) {
                    return element.getAttribute('class');
                },
            },
            style: {
                renderHTML(attrs) {
                    return {
                        style: attrs.style
                            ? Object.entries(attrs.style).reduce((acc, [property, value]) => `${snakeCaseToCamelCase(property)}:${value};${acc}`, '')
                            : undefined,
                    };
                },
                parseHTML(element) {
                    const style = element.getAttribute('style');
                    if (style) {
                        return styleStringToCSSPropertiesObject(style);
                    }
                    return undefined;
                },
            },
            loading: {
                renderHTML(attrs) {
                    return { loading: attrs.loading };
                },
                parseHTML(element) {
                    return element.getAttribute('loading');
                },
            },
            sandbox: {
                renderHTML(attrs) {
                    return { sandbox: attrs.sandbox };
                },
                parseHTML(element) {
                    return element.getAttribute('sandbox');
                },
            },
            width: {
                renderHTML(attrs) {
                    return { width: attrs.width };
                },
                parseHTML(element) {
                    return element.getAttribute('width');
                },
            },
            height: {
                renderHTML(attrs) {
                    return { height: attrs.height };
                },
                parseHTML(element) {
                    return element.getAttribute('height');
                },
            },
        };
    },
    addCommands() {
        return {
            showEmbedInput: () => () => {
                this.options.showEmbedInput();
                return true;
            },
            setEmbed: (url) => {
                return ({ commands }) => {
                    return commands.insertContent({ type: this.name, attrs: { url } });
                };
            },
        };
    },
    parseHTML() {
        return [
            {
                tag: 'div[data-mb-embed] iframe',
            },
        ];
    },
    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            { 'data-mb-embed': '' },
            ['iframe', mergeAttributes(HTMLAttributes)],
        ];
    },
});
//# sourceMappingURL=embed.js.map